import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import { push } from 'connected-react-router';
import { parse } from 'qs';
import { CommonSection } from '@flightstats/component-lib';
import { actions } from '../redux';
import FSFlatButton from '../../../src/components/lib/FSFlatButton';
import componentBase from '../../../src/lib/component-base';
import { isValidEmail } from '../../../src/helpers/Validators';
import { addMetaCanonical } from '../../../shared/lib/meta-tags';
import TextInput from '../../../shared/components/TextInput/TextInput';
import EmailTextInput,
{ textInputLabelStyle,
  textInputLabelErrorStyle,
  textInputSpanStyle,
  textInputInputStyle,
} from './shared/EmailTextInput';

const errorDisplayWording = (code) => {
  const val = code.general ? code.general : code;
  const wordings = {
    INVALID_PASSWORD: { message: 'Invalid email/password combination', type: 'email' },
    MISSING_EMAIL: { message: 'The email address provided is invalid', type: 'email' },
    INVALID_EMAIL: { message: 'The email address provided is invalid', type: 'email' },
    PASSWORD_REQUIRED: { message: 'A password must be provided', type: 'password' },
    UNKNOWN_USER: { message: 'There is no account associated with the user', type: 'email' },
    UNKNOWN_ERROR: { message: 'Error', type: 'email' },
  };

  const message = wordings[val];

  if (message) {
    return message;
  }

  console.log('Unknown login error:', code);
  return {
    message: 'Error: Login temporarily unavailable',
    type: 'email',
  };
};

const parseQs = qs => parse(qs, { ignoreQueryPrefix: true });

@connect(
  state => ({
    user: state.Account.user,
    loggedIn: state.Account.loggedIn,
    loginError: state.Account.loginError,
  }),
  actions,
)
@componentBase('Login')
export default class Login extends Component {
  static propTypes = {
    dispatch: PropTypes.func,
    user: PropTypes.object,
    resetAuthState: PropTypes.func,
    location: PropTypes.object,
    login: PropTypes.func,
    logout: PropTypes.func,
    loginError: PropTypes.array,
    loggedIn: PropTypes.bool,
    routerPush: PropTypes.func,
  };

  constructor(props, context) {
    super(props, context);

    this.parsedQs = parseQs(props.location.search);

    this.redirected = false;

    this.setRef = this.setRef.bind(this);

    // refs
    this.email = null;
    this.password = null;
    this.rememberMe = null;
  }

  componentDidMount() {
    this.props.resetAuthState();
  }

  componentWillReceiveProps(nextProps) {
    if (!this.redirected && nextProps.loggedIn) {
      let urlDestination = '/';

      const qs = parseQs(nextProps.location.search);

      if (qs.destination) {
        urlDestination = qs.destination;
      } else if (nextProps.user && !nextProps.user.verified) {
        urlDestination = '/account/verify';
      }

      this.redirected = true;
      this.props.resetAuthState();
      this.props.dispatch(push(urlDestination));
    }
  }

  onEnterKeyPress = (e) => {
    if (e.charCode === 13) {
      this.handleSubmit(e);
    }
    this.lastKeyPressed = e.charCode;
  };

  getMetaTags = () => {
    const title = 'FlightStats - Login';
    const metaWithoutCanonical = {
      title,
    };
    const { location } = this.props;
    const pathname = location && location.pathname;
    return addMetaCanonical(metaWithoutCanonical, pathname);
  };

  setRef = (ref, refName) => {
    this[refName] = ref;
  };

  sanitizedEmail = () => (
    this.email.value && this.email.value.trim().toLowerCase()
  );

  handleSubmit = (e) => {
    e.preventDefault();

    const { rememberMe, password: passwordInput } = this;
    this.props.login(this.sanitizedEmail(), passwordInput.value, rememberMe.checked);
  };

  validateEmail = () => {
    if (this.lastKeyPressed !== 13 && isValidEmail(this.sanitizedEmail())) {
      this.props.resetAuthState();
    }
    this.lastKeyPressed = null;
  };

  renderLoginForm = () => {
    const { loginError } = this.props;
    let error;
    let emailError = '';
    let passwordError = '';

    if (this.props.loginError) {
      const invalidPassword = loginError.filter(e => e === 'INVALID_PASSWORD');
      if (!invalidPassword) {
        this.email.value = '';
      }
      error = errorDisplayWording(loginError[0]);
      if (error) {
        switch (error.type) {
          case 'email':
            emailError = error.message;
            break;
          case 'password':
            passwordError = error.message;
            break;
          default:
            break;
        }
      }
    }

    return (
      <div className='innerContent'>
        <form
          onSubmit={this.handleSubmit}
          onKeyPress={this.onEnterKeyPress}
          method='POST'
        >
          <div className='innerForm'>
            <EmailTextInput
              errorMessage={emailError}
              inputRef={ref => this.setRef(ref, 'email')}
              validateEmail={this.validateEmail}
            />
            <p className='email-note'>
              NOTE: THIS EMAIL WILL BE THE EMAIL USED DURING REGISTRATION
            </p>
            <TextInput
              errorMessage={passwordError}
              inputClassName='account-input'
              inputRef={ref => this.setRef(ref, 'password')}
              inputStyle={textInputInputStyle}
              label='Password'
              labelErrorStyle={textInputLabelErrorStyle}
              labelStyle={textInputLabelStyle}
              placeholder='•••••••••'
              spanStyle={textInputSpanStyle}
              type='password'
            />
            <div className='account-secondary-div'>
              <input
                type='checkbox'
                name='rememberMe'
                ref={ref => this.setRef(ref, 'rememberMe')}
                defaultChecked
                style={{ margin: '10px 3px 10px 0', display: 'inline-block' }}
              />
              <div
                className='account-input-title'
                style={{ display: 'inline-block', paddingLeft: '0', marginLeft: '2px' }}
              >
                Remember Me
              </div>
              <Link
                to='/account/forgot-password'
                className='account-link'
                style={{ margin: '5px 0', textDecoration: 'underline', float: 'right' }}
              >
                Forgot Password?
              </Link>
            </div>
          </div>
          <div className='innerPadding'>
            <FSFlatButton
              label='LOG IN'
              onClick={this.handleSubmit}
              style={FSFlatButton.style({ minWidth: '200px' })}
            />
          </div>
        </form>
        <div className='innerPadding'>
          <p className='account-no-account-text'>
            Don&apos;t have a FlightStats account?
          </p>
          <Link
            key='createNewAccount'
            to='/register/plan'
            className='account-link'
            style={{ textDecoration: 'underline' }}
          >
            Create a New Account
          </Link>
        </div>
      </div>
    );
  }

  render() {
    const meta = this.getMetaTags();
    return (
      <div className='LoginContainer'>
        <div className='sharedAccountFormContainer'>
          <Helmet {...meta} />
          <h1 className='accountTitle'>Login to FlightStats</h1>
          <CommonSection title='Login'>
            {this.renderLoginForm()}
          </CommonSection>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./modules/Account/components/Login.js